/**
 * ClienteFormPanel — cadastro/edição de Cliente (drawer em 4 passos), estilo v2.
 *
 * Layout fiel à referência oficial de design:
 *   integracao-nova/crm/titam-crm-cadastro-cliente-v2.png
 *
 * Um Cliente É um `contact` (mesma tabela/endpoints do Lead) — este painel
 * troca só a apresentação (wizard Contato → Empresa → Endereço →
 * Classificação, com barra de progresso) por cima dos mesmos campos que o
 * LeadModal já grava. Reusa getCrmContact/createCrmContact/updateCrmContact/
 * deleteCrmContact/getCrmMembers/createCrmContactActivity — nenhum endpoint
 * novo.
 *
 * Três campos da imagem não têm coluna própria no schema (Cargo, Segmento,
 * Número de colaboradores) e um é totalmente livre ("Adicionar campo"): em
 * vez de descartar o que o usuário digita, eles são serializados num bloco
 * delimitado dentro de `anotacoes` (mergeCustomFields/parseCustomFields) —
 * ficam visíveis na aba Observações do perfil do cliente e sobrevivem a
 * edições seguintes sem duplicar.
 *
 * "Aplicar cadência" (Automação inicial) também não tem motor de cadência no
 * backend; ao criar o cliente com uma cadência selecionada, este painel cria
 * UMA atividade inicial (createCrmContactActivity) com o rótulo da cadência —
 * uma automação real, só que de um passo, em vez de uma miragem visual.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import Icon from '../../components/icons.jsx'
import {
  getCrmContact,
  createCrmContact,
  updateCrmContact,
  deleteCrmContact,
  getCrmMembers,
  createCrmContactActivity,
} from '../../db/api.js'
import { STATUS_COLORS, ConfirmDialog } from './panelKit.jsx'
import './clienteForm.css'

const BR_STATES = [
  ['AC', 'Acre'], ['AL', 'Alagoas'], ['AP', 'Amapá'], ['AM', 'Amazonas'],
  ['BA', 'Bahia'], ['CE', 'Ceará'], ['DF', 'Distrito Federal'], ['ES', 'Espírito Santo'],
  ['GO', 'Goiás'], ['MA', 'Maranhão'], ['MT', 'Mato Grosso'], ['MS', 'Mato Grosso do Sul'],
  ['MG', 'Minas Gerais'], ['PA', 'Pará'], ['PB', 'Paraíba'], ['PR', 'Paraná'],
  ['PE', 'Pernambuco'], ['PI', 'Piauí'], ['RJ', 'Rio de Janeiro'], ['RN', 'Rio Grande do Norte'],
  ['RS', 'Rio Grande do Sul'], ['RO', 'Rondônia'], ['RR', 'Roraima'], ['SC', 'Santa Catarina'],
  ['SP', 'São Paulo'], ['SE', 'Sergipe'], ['TO', 'Tocantins'],
]

const STEPS = [
  { id: 'contato', label: 'Contato', icon: 'user' },
  { id: 'empresa', label: 'Empresa', icon: 'briefcase' },
  { id: 'endereco', label: 'Endereço', icon: 'geo' },
  { id: 'classificacao', label: 'Classificação', icon: 'tag' },
]

const ORIGEM_OPTS = ['Indicação', 'Site', 'Redes sociais', 'Evento', 'Cold outreach', 'Anúncio', 'Parceria', 'Outro']

const STATUS_OPTS = [
  ['prospect', 'Prospect'], ['lead', 'Lead'], ['negociacao', 'Negociação'],
  ['cliente', 'Cliente'], ['recorrente', 'Recorrente'],
  ['churned', 'Encerrado'], ['arquivado', 'Arquivado'],
]

const CADENCIA_OPTS = [
  { id: '', label: 'Nenhuma' },
  { id: 'prospeccao', label: 'Prospecção inicial' },
  { id: 'nutricao', label: 'Nutrição' },
  { id: 'reativacao', label: 'Reativação' },
]

const TAG_SUGESTOES_PADRAO = ['b2b', 'b2c', 'consultoria', 'saas', 'varejo', 'prospecção', 'e-commerce']

// Campos sem coluna própria vivem num bloco delimitado dentro de `anotacoes`,
// preservando o resto do texto do usuário ao salvar de novo.
const CUSTOM_BLOCK_RE = /\n?—— Campos personalizados ——[\s\S]*?—— \/Campos personalizados ——\n?/

function parseCustomFields(anotacoes) {
  const m = (anotacoes || '').match(CUSTOM_BLOCK_RE)
  const out = { cargo: '', segmento: '', colaboradores: '', extras: [] }
  if (!m) return out
  const block = m[0]
  out.cargo = block.match(/Cargo: (.*)/)?.[1]?.trim() || ''
  out.segmento = block.match(/Segmento: (.*)/)?.[1]?.trim() || ''
  out.colaboradores = block.match(/Colaboradores: (.*)/)?.[1]?.trim() || ''
  const extraRe = /Outro \((.*?)\): (.*)/g
  let em
  while ((em = extraRe.exec(block))) out.extras.push({ label: em[1], value: em[2] })
  return out
}

function mergeCustomFields(anotacoes, { cargo, segmento, colaboradores, extras }) {
  const base = (anotacoes || '').replace(CUSTOM_BLOCK_RE, '').trim()
  const lines = []
  if (cargo?.trim()) lines.push(`Cargo: ${cargo.trim()}`)
  if (segmento?.trim()) lines.push(`Segmento: ${segmento.trim()}`)
  if (colaboradores?.toString().trim()) lines.push(`Colaboradores: ${colaboradores.toString().trim()}`)
  extras.forEach((e) => { if (e.label?.trim() && e.value?.trim()) lines.push(`Outro (${e.label.trim()}): ${e.value.trim()}`) })
  if (!lines.length) return base || null
  const block = `—— Campos personalizados ——\n${lines.join('\n')}\n—— /Campos personalizados ——`
  return base ? `${base}\n\n${block}` : block
}

const iniciais = (nome) => String(nome || '?').trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || '?'

const EMPTY_FORM = {
  nome: '', email: '', telefone: '', cargo: '', linkedin: '',
  empresa: '', url: '',
  pais: 'Brasil', cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
  origem: ORIGEM_OPTS[0], owner_id: '', tags: [], status: 'cliente',
  cadencia: '', segmento: '', colaboradores: '',
  criarComoContatoPrincipal: true,
}

export default function ClienteFormPanel({
  contactId, onClose, onSaved, onCreatedDeal,
  initialStatus = 'cliente', tagsDisponiveis = [], entityLabel = 'cliente',
  // Usados pelo fluxo "negócio ganho → transformar em cliente" (DealWinFlow):
  // status fixo, sem excluir e por cima do painel do negócio.
  forceStatus = null, allowDelete = true, elevated = false,
}) {
  const isEdit = !!contactId
  const [form, setForm] = useState({ ...EMPTY_FORM, status: forceStatus || initialStatus })
  const [extras, setExtras] = useState([])
  const [step, setStep] = useState('contato')
  const [members, setMembers] = useState([])
  const [cepLoading, setCepLoading] = useState(false)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [shown, setShown] = useState(false)
  const [tagMenuOpen, setTagMenuOpen] = useState(false)
  const [ownerMenuOpen, setOwnerMenuOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const tagBoxRef = useRef(null)
  const ownerBoxRef = useRef(null)

  useEffect(() => { setShown(true) }, [])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    getCrmMembers().then(setMembers).catch(() => {})
  }, [])

  useEffect(() => {
    if (!isEdit) return
    let alive = true
    getCrmContact(contactId)
      .then((c) => {
        if (!alive) return
        const custom = parseCustomFields(c.anotacoes)
        setForm({
          ...EMPTY_FORM,
          ...c,
          telefone: c.telefone || '',
          linkedin: c.linkedin || '',
          origem: c.origem || ORIGEM_OPTS[0],
          owner_id: c.owner_id || '',
          tags: Array.isArray(c.tags) ? c.tags : [],
          status: forceStatus || c.status || 'cliente',
          cargo: custom.cargo,
          segmento: custom.segmento,
          colaboradores: custom.colaboradores,
          criarComoContatoPrincipal: true,
        })
        setExtras(custom.extras)
      })
      .catch((e) => alive && setErr(e.message))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [isEdit, contactId])

  // fecha os combobox ao clicar fora
  useEffect(() => {
    const onDown = (e) => {
      if (tagMenuOpen && tagBoxRef.current && !tagBoxRef.current.contains(e.target)) setTagMenuOpen(false)
      if (ownerMenuOpen && ownerBoxRef.current && !ownerBoxRef.current.contains(e.target)) setOwnerMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [tagMenuOpen, ownerMenuOpen])

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }))
    if (k === 'cep') maybeLookupCep(v)
  }

  const maybeLookupCep = async (v) => {
    const digits = (v || '').replace(/\D/g, '')
    if (digits.length !== 8) return
    setCepLoading(true)
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`)
      const d = await res.json()
      if (d && !d.erro) {
        setForm((f) => ({
          ...f,
          logradouro: d.logradouro || f.logradouro,
          bairro: d.bairro || f.bairro,
          cidade: d.localidade || f.cidade,
          estado: d.uf || f.estado,
          pais: f.pais || 'Brasil',
        }))
      }
    } catch { /* silencioso */ } finally { setCepLoading(false) }
  }

  const addTag = (t) => {
    const v = (t || '').trim()
    if (!v) return
    setForm((f) => (f.tags.includes(v) ? f : { ...f, tags: [...f.tags, v] }))
  }
  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }))

  const [tagInput, setTagInput] = useState('')
  const onTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(tagInput)
      setTagInput('')
    } else if (e.key === 'Backspace' && !tagInput && form.tags.length) {
      removeTag(form.tags[form.tags.length - 1])
    }
  }

  const tagSuggestions = useMemo(() => {
    const pool = [...new Set([...tagsDisponiveis, ...TAG_SUGESTOES_PADRAO])]
    return pool.filter((t) => !form.tags.includes(t) && t.toLowerCase().includes(tagInput.trim().toLowerCase()))
  }, [tagsDisponiveis, form.tags, tagInput])

  const addExtra = () => setExtras((e) => [...e, { label: '', value: '' }])
  const setExtra = (i, k, v) => setExtras((e) => e.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)))
  const removeExtra = (i) => setExtras((e) => e.filter((_, idx) => idx !== i))

  // progresso: fração dos campos-chave preenchidos (aproxima o "% completo" da referência)
  const progress = useMemo(() => {
    const tracked = [form.nome, form.email, form.telefone, form.empresa, form.origem, form.owner_id, form.status]
    const filled = tracked.filter((v) => v && String(v).trim()).length
    return Math.round((filled / tracked.length) * 100)
  }, [form.nome, form.email, form.telefone, form.empresa, form.origem, form.owner_id, form.status])

  const stepIndex = STEPS.findIndex((s) => s.id === step)
  const selectedOwner = members.find((m) => m.id === form.owner_id)

  const buildPayload = () => ({
    nome: form.nome.trim(),
    email: form.email?.trim() || null,
    telefone: form.telefone?.trim() || null,
    linkedin: form.linkedin?.trim() || null,
    empresa: form.empresa?.trim() || null,
    url: form.url?.trim() || null,
    pais: form.pais?.trim() || null,
    cep: form.cep?.trim() || null,
    logradouro: form.logradouro?.trim() || null,
    numero: form.numero?.trim() || null,
    complemento: form.complemento?.trim() || null,
    bairro: form.bairro?.trim() || null,
    cidade: form.cidade?.trim() || null,
    estado: form.estado || null,
    origem: form.origem || null,
    owner_id: form.owner_id || null,
    tags: form.tags.length ? form.tags : null,
    status: form.status || null,
    anotacoes: mergeCustomFields(form.anotacoes, {
      cargo: form.cargo, segmento: form.segmento, colaboradores: form.colaboradores, extras,
    }),
  })

  const save = async (mode) => {
    if (saving) return
    if (!form.nome?.trim()) { setStep('contato'); setErr('Nome completo é obrigatório.'); return }
    if (!form.email?.trim()) { setStep('contato'); setErr('E-mail é obrigatório.'); return }
    if (!form.telefone?.trim()) { setStep('contato'); setErr('Telefone/WhatsApp é obrigatório.'); return }
    setSaving(true); setErr(null)
    try {
      const payload = buildPayload()
      const saved = isEdit
        ? await updateCrmContact(contactId, payload)
        : await createCrmContact(payload)

      if (!isEdit && form.cadencia) {
        const cad = CADENCIA_OPTS.find((c) => c.id === form.cadencia)
        createCrmContactActivity(saved.id, {
          titulo: `Cadência: ${cad?.label || form.cadencia}`,
          tipo: 'tarefa', prioridade: 'media', status: 'pendente',
        }).catch(() => {})
      }

      if (mode === 'deal') onCreatedDeal?.(saved)
      onSaved?.(saved)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  const handleDelete = async () => {
    if (!isEdit) return
    setSaving(true); setErr(null)
    try { await deleteCrmContact(contactId); onSaved?.() }
    catch (e) { setErr(e.message); setSaving(false); setDeleteOpen(false) }
  }

  return (
    <div className={`cff-overlay${elevated ? ' cff-overlay--top' : ''}`} onMouseDown={onClose}>
      <div
        className="cff-drawer"
        style={{ transform: shown ? 'translateX(0)' : 'translateX(100%)' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cff-head">
          <div>
            <h3 className="cff-title">{isEdit ? `Editar ${entityLabel}` : `Novo ${entityLabel}`}</h3>
            <div className="cff-sub">
              {isEdit ? `Atualize os dados do ${entityLabel}.` : 'Cadastre os dados essenciais. Você poderá completar o perfil depois.'}
            </div>
          </div>
          <button className="cff-close" onClick={onClose} title="Fechar (Esc)">
            <Icon n="x" s={16} sw={2} />
          </button>
        </div>

        <div className="cff-stepper">
          {STEPS.map((s, i) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center' }}>
              <button
                type="button"
                className={`cff-step${step === s.id ? ' active' : ''}${i < stepIndex ? ' done' : ''}`}
                onClick={() => setStep(s.id)}
              >
                <span className="cff-step-dot">
                  {i < stepIndex ? <Icon n="check" s={12} sw={2.4} /> : i + 1}
                </span>
                <span className="cff-step-label">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <span className="cff-step-sep"><Icon n="chevron_r" s={14} /></span>}
            </div>
          ))}
        </div>
        <div className="cff-progress-row">
          <span className="cff-progress-pct">{progress}% completo</span>
        </div>
        <div className="cff-progress-track">
          <span className="cff-progress-fill" style={{ width: `${progress}%` }} />
        </div>

        {err && <div className="cff-error" style={{ marginTop: 14 }}>{err}</div>}

        {loading ? (
          <div className="cff-muted">Carregando…</div>
        ) : (
          <div className="cff-body">
            {step === 'contato' && (
              <>
                <div className="cff-section">
                  <div className="cff-sec-title">Identificação</div>
                  <div className="cff-grid">
                    <div className="cff-field">
                      <label className="cff-label">Nome completo<span className="cff-req">*</span></label>
                      <input className="cff-input" placeholder="Ex.: João da Silva" autoFocus
                        value={form.nome} onChange={(e) => set('nome', e.target.value)} />
                    </div>
                    <div className="cff-field">
                      <label className="cff-label">E-mail<span className="cff-req">*</span></label>
                      <input className="cff-input" type="email" placeholder="Ex.: joao@empresa.com"
                        value={form.email} onChange={(e) => set('email', e.target.value)} />
                    </div>
                    <div className="cff-field">
                      <label className="cff-label">
                        Telefone / WhatsApp<span className="cff-req">*</span>
                      </label>
                      <div className="cff-phone">
                        <span className="cff-flag">🇧🇷 +55</span>
                        <input placeholder="(11) 99999-9999"
                          value={form.telefone} onChange={(e) => set('telefone', e.target.value)} />
                      </div>
                    </div>
                    <div className="cff-field">
                      <label className="cff-label">Cargo</label>
                      <input className="cff-input" placeholder="Ex.: Gerente Comercial"
                        value={form.cargo} onChange={(e) => set('cargo', e.target.value)} />
                    </div>
                    <div className="cff-field">
                      <label className="cff-label">LinkedIn</label>
                      <input className="cff-input" placeholder="https://www.linkedin.com/in/..."
                        value={form.linkedin} onChange={(e) => set('linkedin', e.target.value)} />
                    </div>
                  </div>
                </div>
              </>
            )}

            {step === 'empresa' && (
              <div className="cff-section">
                <div className="cff-sec-title">Empresa</div>
                <div className="cff-grid">
                  <div className="cff-field cff-span2 cff-search-field">
                    <div className="cff-label-row">
                      <label className="cff-label">Empresa<span className="cff-req">*</span></label>
                      <button type="button" className="cff-link-btn"
                        onClick={() => document.getElementById('cff-empresa-input')?.focus()}>
                        <Icon n="plus" s={13} sw={2.2} /> Criar empresa
                      </button>
                    </div>
                    <span className="cff-search-icon"><Icon n="search" s={15} /></span>
                    <input id="cff-empresa-input" className="cff-input" placeholder="Buscar empresa…"
                      value={form.empresa} onChange={(e) => set('empresa', e.target.value)} />
                  </div>
                  <div className="cff-field cff-span2">
                    <label className="cff-label">Site</label>
                    <input className="cff-input" placeholder="Ex.: www.empresa.com.br"
                      value={form.url} onChange={(e) => set('url', e.target.value)} />
                  </div>
                  <div className="cff-field">
                    <label className="cff-label">Segmento</label>
                    <input className="cff-input" placeholder="Ex.: Consultoria"
                      value={form.segmento} onChange={(e) => set('segmento', e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            {step === 'endereco' && (
              <div className="cff-section">
                <div className="cff-sec-title">Endereço</div>
                <div className="cff-grid">
                  <div className="cff-field">
                    <label className="cff-label">País</label>
                    <input className="cff-input" placeholder="Brasil"
                      value={form.pais} onChange={(e) => set('pais', e.target.value)} />
                  </div>
                  <div className="cff-field">
                    <label className="cff-label">CEP{cepLoading ? ' · buscando…' : ''}</label>
                    <input className="cff-input" placeholder="12345-678"
                      value={form.cep} onChange={(e) => set('cep', e.target.value)} />
                  </div>
                  <div className="cff-field cff-span2">
                    <label className="cff-label">Endereço</label>
                    <input className="cff-input" placeholder="Av. Paulista"
                      value={form.logradouro} onChange={(e) => set('logradouro', e.target.value)} />
                  </div>
                  <div className="cff-field">
                    <label className="cff-label">Número</label>
                    <input className="cff-input" placeholder="123"
                      value={form.numero} onChange={(e) => set('numero', e.target.value)} />
                  </div>
                  <div className="cff-field">
                    <label className="cff-label">Complemento</label>
                    <input className="cff-input" placeholder="Apto 101"
                      value={form.complemento} onChange={(e) => set('complemento', e.target.value)} />
                  </div>
                  <div className="cff-field">
                    <label className="cff-label">Bairro</label>
                    <input className="cff-input" placeholder="Centro"
                      value={form.bairro} onChange={(e) => set('bairro', e.target.value)} />
                  </div>
                  <div className="cff-field">
                    <label className="cff-label">Cidade</label>
                    <input className="cff-input" placeholder="São Paulo"
                      value={form.cidade} onChange={(e) => set('cidade', e.target.value)} />
                  </div>
                  <div className="cff-field">
                    <label className="cff-label">UF</label>
                    <select className="cff-select" value={form.estado} onChange={(e) => set('estado', e.target.value)}>
                      <option value="">— selecione —</option>
                      {BR_STATES.map(([uf, nome]) => <option key={uf} value={uf}>{nome} ({uf})</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {step === 'classificacao' && (
              <>
                <div className="cff-section">
                  <div className="cff-sec-title">Classificação comercial</div>
                  <div className="cff-grid">
                    <div className="cff-field">
                      <label className="cff-label">Origem<span className="cff-req">*</span></label>
                      <select className="cff-select" value={form.origem} onChange={(e) => set('origem', e.target.value)}>
                        {[...new Set([...ORIGEM_OPTS, form.origem].filter(Boolean))].map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>

                    <div className="cff-field" ref={ownerBoxRef}>
                      <label className="cff-label">Responsável<span className="cff-req">*</span></label>
                      <button type="button" className="cff-combo-btn" onClick={() => setOwnerMenuOpen((v) => !v)}>
                        {selectedOwner ? (
                          <>
                            <span className="cff-combo-avatar">{iniciais(selectedOwner.nome)}</span>
                            <span className="cff-combo-name">{selectedOwner.nome}</span>
                          </>
                        ) : <span className="cff-combo-name cff-combo-placeholder">Selecione…</span>}
                        <span className="cff-combo-chevron"><Icon n="chevron_d" s={14} /></span>
                      </button>
                      {ownerMenuOpen && (
                        <div className="cff-combo-menu">
                          {members.length === 0 && <div className="cff-hint" style={{ padding: '6px 8px' }}>Nenhum membro encontrado.</div>}
                          {members.map((m) => (
                            <button key={m.id} type="button" className="cff-combo-item"
                              onClick={() => { set('owner_id', m.id); setOwnerMenuOpen(false) }}>
                              <span className="cff-combo-avatar">{iniciais(m.nome)}</span>
                              {m.nome}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="cff-field cff-span2" ref={tagBoxRef}>
                      <label className="cff-label">Tags</label>
                      <div className="cff-tags-box">
                        {form.tags.map((t) => (
                          <span key={t} className="cff-chip">
                            {t}
                            <button type="button" onClick={() => removeTag(t)}><Icon n="x" s={11} sw={2.4} /></button>
                          </span>
                        ))}
                        <input
                          className="cff-tags-input"
                          placeholder={form.tags.length ? '' : 'Selecione uma ou mais tags'}
                          value={tagInput}
                          onFocus={() => setTagMenuOpen(true)}
                          onChange={(e) => { setTagInput(e.target.value); setTagMenuOpen(true) }}
                          onKeyDown={onTagKeyDown}
                        />
                        <button type="button" className="cff-tags-toggle" onClick={() => setTagMenuOpen((v) => !v)}>
                          <Icon n="chevron_d" s={14} />
                        </button>
                        {tagMenuOpen && tagSuggestions.length > 0 && (
                          <div className="cff-tags-menu">
                            {tagSuggestions.map((t) => (
                              <button key={t} type="button"
                                onClick={() => { addTag(t); setTagInput(''); setTagMenuOpen(false) }}>{t}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="cff-field cff-status-field">
                      <label className="cff-label">Status<span className="cff-req">*</span></label>
                      <span className="cff-status-dot" style={{ background: STATUS_COLORS[form.status] || '#98a2b3' }} />
                      <select className="cff-select" value={form.status} disabled={!!forceStatus} onChange={(e) => set('status', e.target.value)}>
                        {STATUS_OPTS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="cff-section">
                  <div className="cff-sec-title">Automação inicial (opcional)</div>
                  <div className="cff-field">
                    <label className="cff-label">Aplicar cadência</label>
                    <select className="cff-select" value={form.cadencia} onChange={(e) => set('cadencia', e.target.value)}>
                      {CADENCIA_OPTS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <div className="cff-hint">Será criada automaticamente uma primeira tarefa de acompanhamento para a cadência selecionada.</div>
                  </div>
                </div>

                <div className="cff-section">
                  <div className="cff-sec-title">Campos personalizados</div>
                  <div className="cff-grid">
                    <div className="cff-field">
                      <label className="cff-label">Número de colaboradores</label>
                      <input className="cff-input" type="number" min="0" placeholder="Ex.: 20"
                        value={form.colaboradores} onChange={(e) => set('colaboradores', e.target.value)} />
                    </div>
                  </div>

                  {extras.map((ex, i) => (
                    <div key={i} className="cff-extra-row">
                      <input className="cff-input" placeholder="Nome do campo"
                        value={ex.label} onChange={(e) => setExtra(i, 'label', e.target.value)} />
                      <input className="cff-input" placeholder="Valor"
                        value={ex.value} onChange={(e) => setExtra(i, 'value', e.target.value)} />
                      <button type="button" className="cff-extra-remove" onClick={() => removeExtra(i)} title="Remover campo">
                        <Icon n="x" s={14} sw={2} />
                      </button>
                    </div>
                  ))}

                  <div style={{ marginTop: 12 }}>
                    <button type="button" className="cff-link-btn" onClick={addExtra}>
                      <Icon n="plus" s={13} sw={2.2} /> Adicionar campo
                    </button>
                  </div>
                </div>

                <div className="cff-checkbox-row">
                  <input id="cff-principal" type="checkbox" className="cff-checkbox"
                    checked={form.criarComoContatoPrincipal}
                    onChange={(e) => set('criarComoContatoPrincipal', e.target.checked)} />
                  <label htmlFor="cff-principal" className="cff-checkbox-label">
                    Criar também como contato principal da empresa
                  </label>
                </div>
              </>
            )}
          </div>
        )}

        <div className="cff-footer">
          {isEdit && allowDelete && (
            <button type="button" className="cff-btn cff-btn-danger" onClick={() => setDeleteOpen(true)} disabled={saving}>
              Excluir
            </button>
          )}
          <div className="cff-footer-right">
            <button type="button" className="cff-btn cff-btn-ghost" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            {!isEdit && (
              <button type="button" className="cff-btn cff-btn-outline" onClick={() => save('deal')} disabled={saving}>
                Criar e adicionar negócio
              </button>
            )}
            <button type="button" className="cff-btn cff-btn-primary" onClick={() => save('save')} disabled={saving}>
              {saving ? 'Salvando…' : isEdit ? 'Salvar alterações' : `Criar ${entityLabel}`}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        title={`Excluir ${entityLabel}`}
        message={`Excluir este ${entityLabel}? Os vínculos com negócios serão removidos. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        danger
        busy={saving}
        onConfirm={handleDelete}
        onCancel={() => { if (!saving) setDeleteOpen(false) }}
      />
    </div>
  )
}
